import {Injectable} from '@angular/core';
import {PostService} from '../post.service';
import {Router} from '@angular/router';
import {createEffect, Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {map, switchMap, tap, catchError, mergeMap} from 'rxjs/operators';
import {of} from 'rxjs';

import * as fromApp from './../../../store/app.reducers';

import * as PostActions from './post.actions';
import * as ProfileActions from '../../store/profile.actions';
import * as ErrorActions from '../../../error/store/error.actions';

import {HttpErrorResponse} from '@angular/common/http';
import {Post} from '../post.model';
import {Profile} from '../../profile.model';
import {SettingsService} from '../../../core/settings.service';
import {ToastrService} from 'ngx-toastr';

@Injectable()
export class PostEffects {

  getPostDetail = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_POST_DETAIL),
      map(
        (data: {type: string, payload: {id: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'detail', value: false})
          );
          return this.postService.getPostDetail(param.id).pipe(
            mergeMap(
              (response: Profile) => {
                return [{
                  type: PostActions.SET_POST_DETAIL,
                  payload: response
                }, {
                  type: ProfileActions.SET_LOADER,
                  payload: {module: 'audit', box: 'detail', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getPostExport = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_EXPORT_POST),
      map(
        (data: {type: string, payload: {id: number, type: number, params: any}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param:  {id: number, type: number, params: any}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'export', value: false})
          );
          return this.postService.getExportUsers(param.id, param.type).pipe(
            mergeMap(
              (response: any) => {
                const link = this.settingsService.getSetting('apiHost') + 'file.xlsx?' +
                  'filename=' + response.data.filename.replace('#', '').replace('&', '') + '&' +
                  'store_filename=' + response.data.store_filename;
                window.location.replace(link);
                return [{
                  type: ProfileActions.SET_LOADER,
                  payload: {module: 'audit', box: 'export', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getPostProfileMedia = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_PROFILE_MEDIA),
      map(
        (data: {type: string, payload: {id: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'media', value: false})
          );
          return this.postService.getPostProfileMedia(param.id).pipe(
            mergeMap(
              (response: Profile) => {
                return [{
                  type: ProfileActions.SET_PROFILE_MEDIA,
                  payload: response
                }, {
                  type: ProfileActions.SET_LOADER,
                  payload: {module: 'audit', box: 'media', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getAudit = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_AUDIT),
      map(
        (data: {type: string, payload: number}) => {
          return data.payload;
        }
      ),
      switchMap(
        (id: number) => {
          return this.postService.getPost(id).pipe(
            map(
              (response: Profile) => {
                return {
                  type: ProfileActions.SET_AUDIT,
                  payload: response
                };
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getProfileProgress = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_POST_PROGRESS),
      map(
        (data: {type: string, payload: {id: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number}) => {
          return this.postService.getPostProgress(param.id).pipe(
            map(
              (response: Profile) => {
                return {
                  type: ProfileActions.SET_PROFILE_PROGRESS,
                  payload: response
                };
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getFollowerStat = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_FOLLOWER_STAT),
      map(
        (data: {type: string, payload: {id: number, type: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number, type: number}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'followerStat', value: false})
          );
          return this.postService.getFollowerStat(param.id, param.type).pipe(
            mergeMap(
              (response: Profile) => {
                return [{
                  type: ProfileActions.SET_FOLLOWER_STAT,
                  payload: response
                }, {
                  type: ProfileActions.SET_CHARTS_RELOAD,
                  payload: ProfileActions.SET_FOLLOWER_STAT
                }, {
                  type: ProfileActions.SET_LOADER,
                  payload: {module: 'audit', box: 'followerStat', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getMediaStat = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_MEDIA_STAT),
      map(
        (data: {type: string, payload: {id: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'mediaStat', value: false})
          );
          return this.postService.getMediaStat(param.id).pipe(
            mergeMap(
              (response: Profile) => {
                return [{
                  type: ProfileActions.SET_MEDIA_STAT,
                  payload: response
                }, {
                  type: ProfileActions.SET_CHARTS_RELOAD,
                  payload: ProfileActions.SET_MEDIA_STAT
                }, {
                  type: ProfileActions.SET_LOADER,
                  payload: {module: 'audit', box: 'mediaStat', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getAudienceStat = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_AUDIENCE_STAT),
      map(
        (data: {type: string, payload: {id: number, type: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number, type: number}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'audienceStat', value: false})

          );
          return this.postService.getAudienceStat(param.id, param.type).pipe(
            mergeMap(
              (response: Profile) => {
                return [
                  {
                    type: ProfileActions.SET_AUDIENCE_STAT,
                    payload: response
                  },
                  {
                    type: ProfileActions.SET_CHARTS_RELOAD,
                    payload: ProfileActions.SET_AUDIENCE_STAT
                  },
                  {
                    type: ProfileActions.SET_LOADER,
                    payload: {module: 'audit', box: 'audienceStat', value: true}
                  }
                ];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );

  getAudienceLocation = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_AUDIENCE_LOCATION),
      map(
        (data: {type: string, payload: {id: number, type: number}}) => {
          return data.payload;
        }
      ),
      switchMap(
        (param: {id: number, type: number}) => {
          this.store.dispatch(
            new ProfileActions.SetLoader({module: 'audit', box: 'audienceLocation', value: false})
          );
          return this.postService.getAudienceLocation(param.id, param.type).pipe(
            mergeMap(
              (response: Profile) => {
                return [{
                  type: ProfileActions.SET_AUDIENCE_LOCATION,
                  payload: response
                }, {
                  type: ProfileActions.SET_CHARTS_RELOAD,
                  payload: ProfileActions.SET_AUDIENCE_LOCATION
                }, {
                  type: ProfileActions.SET_LOADER,
                  payload: {module: 'audit', box: 'audienceLocation', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: true}
  );


  getAudits = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.GET_AUDITS),
      switchMap(
        () => {
          this.store.dispatch(
            new PostActions.SetLoader({module: 'auditList', box: 'list', value: false})
          );
          return this.postService.getPosts().pipe(
            mergeMap(
              (response: Post[]) => {
                if (response.length === 1) {
                  // this.router.navigate(['audit', response[0].id]);
                }
                return [{
                  type: PostActions.SET_AUDITS,
                  payload: response
                }, {
                  type: PostActions.SET_LOADER,
                  payload: {module: 'auditList', box: 'list', value: true}
                }];
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    )
    , {dispatch: true}
  );

  addAudit = createEffect(() => this.actions$
    .pipe(
      ofType(PostActions.ADD_AUDIT),
      map(
        (data: {type: string, payload: string}) => {
          return data.payload;
        }
      ),
      switchMap(
        (post: string) => {
          return this.postService.addPost(post, {}).pipe(
            tap(
              (response: Post) => {
                this.router.navigate(['/posts']);
                this.notifierService.success('The report has been added!');
              }
            ),
            catchError(
              (err: HttpErrorResponse) => {
                return of({
                  type: ErrorActions.SET_HTTP_ERROR,
                  payload: err
                });
              }
            )
          );
        }
      )
    ), {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private postService: PostService,
    private router: Router,
    private notifierService: ToastrService,
    private store: Store<fromApp.AppState>,
    private settingsService: SettingsService
  ) {}
}
